import { Dispatch } from '@reduxjs/toolkit';
import axiosInstance, { ApiKeys, getApiUrl, logError } from '../../../utils/ApiHelper';
import { STORE_KEYS } from '../constants';
import {
  setCaseDetail,
  setCaseDetailError,
  setCaseDetailLoading
} from '../reducers/caseDetailSlice';
import { ISimilarGeoLocationsPayload } from '@cp/pages/CaseDetails/interfaces/MoreDetails.type';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import { API_STATUS_CODE } from '@cp/src/utils/ApiHelperUtils';

export const fetchAddresses =
  (
    lan: string,
    customerRefrenceId: string,
    caseReferenceId: string,
    caseBusinessVertical: string
  ) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.ADDRESSES);
    dispatch(
      setCaseDetailLoading({
        lan,
        customerRefrenceId,
        key: STORE_KEYS.ADDRESSES,
        loading: true
      })
    );
    axiosInstance
      .get(url, {
        headers: {
          customerreferenceid: customerRefrenceId,
          loanaccountnumber: lan,
          caseReferenceId,
          caseBusinessVertical,
          includeInUptimeMetrics: true
        }
      })
      .then(response => {
        dispatch(
          setCaseDetail({
            lan,
            customerRefrenceId,
            key: STORE_KEYS.ADDRESSES,
            data: response.data
          })
        );
      })
      .catch(err => {
        dispatch(
          setCaseDetailError({
            lan,
            customerRefrenceId,
            key: STORE_KEYS.ADDRESSES,
            error: err
          })
        );
      });
  };

export const fetchGeolocations =
  (
    lan: string,
    customerRefrenceId: string,
    caseReferenceId: string,
    caseBusinessVertical: string
  ) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.GEOLOCATIONS);
    dispatch(
      setCaseDetailLoading({
        lan,
        customerRefrenceId,
        key: STORE_KEYS.GEOLOCATIONS_ADDRESSES,
        loading: true
      })
    );
    axiosInstance
      .get(url, {
        headers: {
          customerreferenceid: customerRefrenceId,
          loanaccountnumber: lan,
          caseReferenceId,
          caseBusinessVertical
        },
        params: {
          includeGroupedAddressesAndGeoLocations: true,
          includeAddressFeedback: true,
          includeFeedbacks: false,
          includeEmiSchedule: false,
          includeRepayments: false,
          includeUngroupedAddresses: false
        }
      })
      .then(response => {
        dispatch(
          setCaseDetail({
            lan,
            customerRefrenceId,
            key: STORE_KEYS.GEOLOCATIONS_ADDRESSES,
            data: response.data
          })
        );
      })
      .catch(err => {
        dispatch(
          setCaseDetailError({
            lan,
            customerRefrenceId,
            key: STORE_KEYS.GEOLOCATIONS_ADDRESSES,
            error: err
          })
        );
      });
  };

export const fetchSimilarGeoLocations =
  ({ lan, customerRefrenceId, clusterId, limit }: ISimilarGeoLocationsPayload) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.SIMILAR_GEOLOCATIONS, { clusterId });
    dispatch(
      setCaseDetailLoading({
        lan,
        customerRefrenceId,
        key: STORE_KEYS.SIMILAR_GEOLOCATIONS,
        loading: true
      })
    );
    axiosInstance
      .get(url, {
        headers: { customerreferenceid: customerRefrenceId, loanaccountnumber: lan },
        params: {
          includeGroupedAddressesAndGeoLocations: true,
          includeAddressFeedback: true,
          includeFeedbacks: false,
          limit: limit
        }
      })
      .then(response => {
        dispatch(
          setCaseDetail({
            lan,
            customerRefrenceId,
            key: STORE_KEYS.SIMILAR_GEOLOCATIONS,
            data: response.data
          })
        );
      })
      .catch(err => {
        dispatch(
          setCaseDetailError({
            lan,
            customerRefrenceId,
            key: STORE_KEYS.SIMILAR_GEOLOCATIONS,
            error: err
          })
        );
      });
  };

export const getPinCodeDetails = async (pinCode: string) => {
  const url = getApiUrl(ApiKeys.GET_PIN_CODES_DETAILS, { pinCode });
  return await axiosInstance.get(url, {
    headers: {
      donotHandleError: true
    }
  });
};

export const addAddress =
  (
    formData: FormData,
    commonPayload: {
      loanAccountNumber: string;
      customerReferenceId: string;
      caseReferenceId: string;
      caseBusinessVertical: string;
    },
    setIsSubmitButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>,
    successCb: () => void
  ) =>
  () => {
    const url = getApiUrl(ApiKeys.ADD_NEW_ADDRESS);
    setIsSubmitButtonDisabled(true);

    return axiosInstance
      .post(url, formData, {
        headers: {
          customerReferenceId: commonPayload.customerReferenceId,
          loanAccountNumber: commonPayload.loanAccountNumber,
          caseBusinessVertical: commonPayload.caseBusinessVertical,
          caseReferenceId: commonPayload.caseReferenceId,
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(response => {
        if (
          response?.status === API_STATUS_CODE.CREATED ||
          response?.status === API_STATUS_CODE.OK
        ) {
          successCb();
          toast('Address added successfully', { type: 'success' });
        } else {
          toast('Address update failed', { type: 'error' });
        }
        setIsSubmitButtonDisabled(false);
      })
      .catch(err => {
        logError(err);
        toast('Address update failed', { type: 'error' });
        setIsSubmitButtonDisabled(false);
      });
  };
